import React, { useState } from "react";
import styled from "styled-components";
import { useTheme } from "../contexts/ThemeContext";
import {
  getAdminDebugPlayerFeatures,
  getAdminDebugModelFeatureNames,
  postAdminDebugModelPredict,
  getAdminDebugTeams,
  getAdminDebugTeamFeatures,
} from "../services/api";

const MODEL_OPTIONS = [
  { value: "all", label: "All models" },
  { value: "total_bases_v0_2", label: "Total Bases v0.2" },
  { value: "tb_v3", label: "TB v3" },
  { value: "hr_v1", label: "HR v1" },
  { value: "hr_v2", label: "HR v2" },
  { value: "hit_v1", label: "Hit v1" },
  { value: "2hit_v1", label: "2+ Hits v1" },
];

/** Single-model options for What-if (no "All models"). */
const MODEL_OPTIONS_ONE = [
  { value: "total_bases_v0_2", label: "Total Bases v0.2" },
  { value: "tb_v3", label: "TB v3" },
  { value: "hr_v1", label: "HR v1" },
  { value: "hr_v2", label: "HR v2" },
  { value: "hit_v1", label: "Hit v1" },
  { value: "2hit_v1", label: "2+ Hits v1" },
];

/** Fallback MLB teams (id, name) when /admin/debug-teams fails (e.g. 403). Sorted by name to match backend. */
const MLB_TEAMS_FALLBACK = [
  { id: 109, name: "Arizona Diamondbacks" },
  { id: 110, name: "Baltimore Orioles" },
  { id: 111, name: "Boston Red Sox" },
  { id: 112, name: "Chicago Cubs" },
  { id: 145, name: "Chicago White Sox" },
  { id: 113, name: "Cincinnati Reds" },
  { id: 114, name: "Cleveland Guardians" },
  { id: 115, name: "Colorado Rockies" },
  { id: 116, name: "Detroit Tigers" },
  { id: 117, name: "Houston Astros" },
  { id: 118, name: "Kansas City Royals" },
  { id: 108, name: "Los Angeles Angels" },
  { id: 119, name: "Los Angeles Dodgers" },
  { id: 146, name: "Miami Marlins" },
  { id: 158, name: "Milwaukee Brewers" },
  { id: 142, name: "Minnesota Twins" },
  { id: 121, name: "New York Mets" },
  { id: 147, name: "New York Yankees" },
  { id: 133, name: "Oakland Athletics" },
  { id: 143, name: "Philadelphia Phillies" },
  { id: 134, name: "Pittsburgh Pirates" },
  { id: 135, name: "San Diego Padres" },
  { id: 137, name: "San Francisco Giants" },
  { id: 136, name: "Seattle Mariners" },
  { id: 138, name: "St. Louis Cardinals" },
  { id: 139, name: "Tampa Bay Rays" },
  { id: 140, name: "Texas Rangers" },
  { id: 141, name: "Toronto Blue Jays" },
  { id: 120, name: "Washington Nationals" },
];

const Container = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 1rem;
  color: ${(props) => props.theme.primaryText};
`;

const Title = styled.h1`
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${(props) => props.theme.secondaryText};
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding: 1.25rem;
  background: ${(props) => props.theme.cardBg || props.theme.secondaryBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  label {
    font-size: 0.9rem;
    color: ${(props) => props.theme.secondaryText};
    white-space: nowrap;
  }
  input {
    padding: 0.55rem 0.75rem;
    font-size: 1rem;
    background: ${(props) => props.theme.inputBg};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: 0.4rem;
    color: ${(props) => props.theme.primaryText};
    min-width: 140px;
  }
  select {
    padding: 0.55rem 2rem 0.55rem 0.75rem;
    font-size: 1rem;
    font-weight: 500;
    min-height: 2.5rem;
    min-width: 180px;
    max-width: 260px;
    background: ${(props) => props.theme.inputBg};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: 0.4rem;
    color: ${(props) => props.theme.primaryText};
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 16 16' fill='%23999'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.65rem center;
  }
  select:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary || "#4299e1"};
    box-shadow: 0 0 0 2px ${(props) => (props.theme.primary ? `${props.theme.primary}40` : "rgba(66, 153, 225, 0.25)")};
  }
  select option {
    font-size: 1rem;
    padding: 0.5rem;
    background: ${(props) => props.theme.inputBg || "#2d3748"};
    color: ${(props) => props.theme.primaryText || "#e2e8f0"};
  }
`;

const SubmitBtn = styled.button`
  padding: 0.5rem 1.25rem;
  font-size: 0.95rem;
  background: ${(props) => props.theme.primary};
  color: ${(props) => props.theme.primaryContrast || "#fff"};
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  background: rgba(229, 62, 62, 0.15);
  border: 1px solid rgba(229, 62, 62, 0.4);
  border-radius: 0.5rem;
  color: #e53e3e;
`;

const Summary = styled.div`
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  background: ${(props) => props.theme.secondaryBg || "rgba(255,255,255,0.06)"};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  font-size: 0.95rem;
  strong {
    color: ${(props) => props.theme.primaryText};
  }
`;

const ModelCard = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: ${(props) => props.theme.cardBg || props.theme.primaryBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
`;

const ModelTitle = styled.h3`
  color: ${(props) => props.theme.primaryText};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const ModelMeta = styled.div`
  font-size: 0.9rem;
  color: ${(props) => props.theme.secondaryText};
  margin-bottom: 0.75rem;
  span { margin-right: 1rem; }
`;

const FeaturesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  th, td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid ${(props) => props.theme.border};
  }
  th {
    color: ${(props) => props.theme.secondaryText};
    font-weight: 600;
  }
  td {
    color: ${(props) => props.theme.primaryText};
  }
  tr:last-child td { border-bottom: none; }
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.primaryText};
  font-size: 1.25rem;
  margin: 2.5rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const WhatIfCard = styled.div`
  padding: 1.25rem;
  background: ${(props) => props.theme.cardBg || props.theme.secondaryBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
  .feature-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .feature-field label {
    font-size: 0.8rem;
    color: ${(props) => props.theme.secondaryText};
    word-break: break-all;
  }
  .feature-field input {
    padding: 0.4rem 0.5rem;
    font-size: 0.9rem;
    background: ${(props) => props.theme.inputBg};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: 0.35rem;
    color: ${(props) => props.theme.primaryText};
  }
`;

const PredictResult = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${(props) => props.theme.secondaryBg || "rgba(255,255,255,0.06)"};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  font-size: 1rem;
  strong { color: ${(props) => props.theme.primaryText}; }
`;

const TeamTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  margin-top: 1rem;
  th, td {
    padding: 0.5rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid ${(props) => props.theme.border};
  }
  th {
    color: ${(props) => props.theme.secondaryText};
    font-weight: 600;
    background: ${(props) => props.theme.secondaryBg || "rgba(255,255,255,0.04)"};
  }
  td {
    color: ${(props) => props.theme.primaryText};
  }
  tr:hover td {
    background: ${(props) => props.theme.tertiaryBg || "rgba(255,255,255,0.03)"};
  }
  .num { text-align: right; }
`;

function AdminModelDebugPage() {
  const { theme } = useTheme();
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [dateStr, setDateStr] = useState(() => todayISO());
  const [playerId, setPlayerId] = useState("");
  const [model, setModel] = useState("all");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // What-if state
  const [whatIfDate, setWhatIfDate] = useState(() => todayISO());
  const [whatIfModel, setWhatIfModel] = useState("hr_v2");
  const [whatIfPlayerId, setWhatIfPlayerId] = useState("");
  const [featureNames, setFeatureNames] = useState([]);
  const [featureValues, setFeatureValues] = useState({});
  const [predictResult, setPredictResult] = useState(null);
  const [predictLoading, setPredictLoading] = useState(false);
  const [predictError, setPredictError] = useState(null);
  const [whatIfMessage, setWhatIfMessage] = useState(null);

  // Team view state
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamViewDate, setTeamViewDate] = useState(() => todayISO());
  const [teamViewTeamId, setTeamViewTeamId] = useState("");
  const [teamViewModel, setTeamViewModel] = useState("total_bases_v0_2");
  const [teamViewDisplayModel, setTeamViewDisplayModel] = useState("");
  const [teamViewLineupFilter, setTeamViewLineupFilter] = useState("all");
  const [teamViewResult, setTeamViewResult] = useState(null);
  const [teamViewLoading, setTeamViewLoading] = useState(false);
  const [teamViewError, setTeamViewError] = useState(null);

  React.useEffect(() => {
    getAdminDebugTeams()
      .then((d) => setTeamOptions(d.teams?.length ? d.teams : MLB_TEAMS_FALLBACK))
      .catch(() => setTeamOptions(MLB_TEAMS_FALLBACK));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!dateStr.trim() || !playerId.trim()) {
      setError("Date and Player ID are required.");
      return;
    }
    setLoading(true);
    try {
      const data = await getAdminDebugPlayerFeatures(dateStr.trim(), playerId.trim(), model);
      if (data.error && !data.models?.length) {
        setError(data.error);
        setResult(null);
      } else {
        setResult(data);
        setError(null);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Request failed.";
      setError(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturesFromPlayer = async () => {
    if (!whatIfDate.trim() || !whatIfPlayerId.trim() || !whatIfModel) {
      setWhatIfMessage("Date, Player ID, and Model are required.");
      return;
    }
    setWhatIfMessage(null);
    setPredictError(null);
    setPredictResult(null);
    try {
      const data = await getAdminDebugPlayerFeatures(whatIfDate.trim(), whatIfPlayerId.trim(), whatIfModel);
      const modelData = data.models?.find((m) => m.model_type === whatIfModel);
      if (!modelData || modelData.error) {
        setWhatIfMessage(modelData?.error || data.error || "Could not load features.");
        return;
      }
      const names = (modelData.features || []).map((f) => f.feature_name);
      const values = {};
      (modelData.features || []).forEach((f) => {
        values[f.feature_name] = f.value != null ? String(f.value) : "";
      });
      setFeatureNames(names);
      setFeatureValues(values);
      setWhatIfMessage("Features loaded. Edit any value and click Predict.");
    } catch (err) {
      setWhatIfMessage(err.response?.data?.error || err.message || "Request failed.");
    }
  };

  const getFeatureList = async () => {
    if (!whatIfDate.trim() || !whatIfModel) {
      setWhatIfMessage("Date and Model are required.");
      return;
    }
    setWhatIfMessage(null);
    setPredictError(null);
    setPredictResult(null);
    try {
      const data = await getAdminDebugModelFeatureNames(whatIfDate.trim(), whatIfModel);
      if (data.error) {
        setWhatIfMessage(data.error);
        return;
      }
      const names = data.feature_names || [];
      const values = {};
      names.forEach((n) => { values[n] = ""; });
      setFeatureNames(names);
      setFeatureValues(values);
      setWhatIfMessage("Enter values for each feature, then click Predict.");
    } catch (err) {
      setWhatIfMessage(err.response?.data?.error || err.message || "Request failed.");
    }
  };

  const handleFeatureChange = (name, value) => {
    setFeatureValues((prev) => ({ ...prev, [name]: value }));
  };

  /** Build CSV from current team view table (respects display model and lineup filter). */
  const downloadTeamViewCsv = () => {
    if (!teamViewResult?.players?.length) return;
    const players = teamViewResult.players.filter(
      (p) => teamViewLineupFilter !== "lineup" || p.in_lineup === true
    );
    const player0 = teamViewResult.players[0];
    const modelData0 = player0.models.find((m) => m.model_type === teamViewDisplayModel) || player0.models[0];
    const featureNames = (modelData0?.features || []).map((f) => f.feature_name);
    const escape = (val) => {
      const s = val == null ? "" : String(val);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = ["Player", "Lineup", "Game ID", ...featureNames, "Prediction", "P(y=1)"];
    const rows = players.map((p) => {
      const modelData = p.models.find((m) => m.model_type === teamViewDisplayModel) || p.models[0];
      const features = modelData?.features || [];
      const featVals = features.map((f) => (f.value != null ? Number(f.value).toFixed(4) : "—"));
      const pred = modelData?.prediction != null ? Number(modelData.prediction) : "—";
      const prob = modelData?.probability != null ? Number(modelData.probability).toFixed(4) : "—";
      return [
        p.player_name ?? p.player_id ?? "—",
        p.batting_order != null ? p.batting_order : "—",
        p.game_id ?? "—",
        ...featVals,
        pred,
        prob,
      ];
    });
    const csvContent = [header.map(escape).join(","), ...rows.map((row) => row.map(escape).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `team-debug-${teamViewResult.date ?? "date"}-${teamViewResult.team_id ?? "team"}-${teamViewDisplayModel || "model"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchTeamView = async () => {
    if (!teamViewDate.trim() || !teamViewTeamId) {
      setTeamViewError("Date and Team are required.");
      return;
    }
    setTeamViewError(null);
    setTeamViewResult(null);
    setTeamViewLoading(true);
    try {
      const data = await getAdminDebugTeamFeatures(teamViewDate.trim(), teamViewTeamId, teamViewModel);
      if (data.error && !data.players?.length) {
        setTeamViewError(data.error);
        setTeamViewResult(null);
      } else {
        setTeamViewError(null);
        setTeamViewResult(data);
        const firstModel = data.players?.[0]?.models?.[0]?.model_type;
        if (firstModel) setTeamViewDisplayModel(firstModel);
      }
    } catch (err) {
      setTeamViewError(err.response?.data?.error || err.message || "Request failed.");
    } finally {
      setTeamViewLoading(false);
    }
  };

  const runPredict = async () => {
    if (!whatIfDate.trim() || !whatIfModel) {
      setPredictError("Date and Model are required.");
      return;
    }
    const overrides = {};
    Object.entries(featureValues).forEach(([name, val]) => {
      const v = typeof val === "string" ? val.trim() : val;
      if (v !== "" && v !== undefined && v !== null) {
        const num = Number(v);
        if (!Number.isNaN(num)) overrides[name] = num;
      }
    });
    setPredictError(null);
    setPredictResult(null);
    setPredictLoading(true);
    try {
      const body = { date: whatIfDate.trim(), model: whatIfModel, overrides };
      if (whatIfPlayerId.trim()) body.player_id = parseInt(whatIfPlayerId.trim(), 10);
      const data = await postAdminDebugModelPredict(body);
      if (data.error) {
        setPredictError(data.error);
      } else {
        setPredictResult(data);
      }
    } catch (err) {
      setPredictError(err.response?.data?.error || err.message || "Request failed.");
    } finally {
      setPredictLoading(false);
    }
  };

  return (
    <Container>
      <Title theme={theme}>Model Debug: Player Features</Title>
      <Subtitle theme={theme}>
        Inspect the features and predictions for a player on a given date. Uses lineup data from S3 and runs the selected model(s). Only available when running locally with ENABLE_DEBUG_PLAYER_FEATURES set.
      </Subtitle>

      <SectionTitle theme={theme} style={{ marginTop: 0 }}>Inspect player features</SectionTitle>
      <Form theme={theme} onSubmit={handleSubmit}>
        <Field theme={theme}>
          <label>Date</label>
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            required
          />
        </Field>
        <Field theme={theme}>
          <label>Player ID</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="e.g. 660271"
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            required
          />
        </Field>
        <Field theme={theme}>
          <label>Model (inspect)</label>
          <select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Model to inspect">
            {MODEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>
        <SubmitBtn type="submit" theme={theme} disabled={loading}>
          {loading ? "Loading…" : "Get features"}
        </SubmitBtn>
      </Form>

      {error && (
        <ErrorBox>{error}</ErrorBox>
      )}

      {result && !result.error && (
        <>
          <Summary theme={theme}>
            <strong>Player:</strong> {result.player_name ?? "—"} &nbsp;|&nbsp;{" "}
            <strong>Game ID:</strong> {result.game_id ?? "—"} &nbsp;|&nbsp;{" "}
            <strong>Date:</strong> {result.date}
          </Summary>

          {result.models?.map((m) => (
            <ModelCard key={m.model_type} theme={theme}>
              <ModelTitle theme={theme}>{m.model_type}</ModelTitle>
              <ModelMeta theme={theme}>
                {m.output_column && <span>Output: {m.output_column}</span>}
                {m.prediction != null && <span>Prediction: {m.prediction}</span>}
                {m.probability != null && <span>P(y=1): {Number(m.probability).toFixed(4)}</span>}
                {m.error && <span style={{ color: "#e53e3e" }}>Error: {m.error}</span>}
              </ModelMeta>
              {m.features?.length > 0 && (
                <FeaturesTable theme={theme}>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.features.map((f, i) => (
                      <tr key={i}>
                        <td>{f.feature_name}</td>
                        <td>{f.value != null ? Number(f.value).toFixed(6) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </FeaturesTable>
              )}
            </ModelCard>
          ))}
        </>
      )}

      <SectionTitle theme={theme}>What-if: predict with custom feature values</SectionTitle>
      <WhatIfCard theme={theme}>
        <p style={{ color: theme.secondaryText, fontSize: "0.9rem", marginBottom: "1rem" }}>
          Load a player&apos;s features as a baseline (then edit) or get an empty feature list. Set values and run the model to see the prediction.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end", marginBottom: "1rem" }}>
          <Field theme={theme}>
            <label>Date</label>
            <input type="date" value={whatIfDate} onChange={(e) => setWhatIfDate(e.target.value)} />
          </Field>
          <Field theme={theme}>
            <label>Model (what-if)</label>
            <select value={whatIfModel} onChange={(e) => setWhatIfModel(e.target.value)} aria-label="Model for what-if prediction">
              {MODEL_OPTIONS_ONE.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>
          <Field theme={theme}>
            <label>Player ID (for Load)</label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="e.g. 660271"
              value={whatIfPlayerId}
              onChange={(e) => setWhatIfPlayerId(e.target.value)}
            />
          </Field>
          <SubmitBtn type="button" theme={theme} onClick={loadFeaturesFromPlayer}>
            Load from player
          </SubmitBtn>
          <SubmitBtn type="button" theme={theme} onClick={getFeatureList}>
            Get feature list
          </SubmitBtn>
        </div>
        {whatIfMessage && (
          <p style={{ color: theme.secondaryText, fontSize: "0.9rem", marginBottom: "0.75rem" }}>{whatIfMessage}</p>
        )}
        {featureNames.length > 0 && (
          <>
            <FeatureGrid theme={theme}>
              {featureNames.map((name) => (
                <div key={name} className="feature-field">
                  <label htmlFor={`feat-${name}`}>{name}</label>
                  <input
                    id={`feat-${name}`}
                    type="text"
                    inputMode="decimal"
                    value={featureValues[name] ?? ""}
                    onChange={(e) => handleFeatureChange(name, e.target.value)}
                    placeholder="0"
                  />
                </div>
              ))}
            </FeatureGrid>
            <SubmitBtn type="button" theme={theme} onClick={runPredict} disabled={predictLoading}>
              {predictLoading ? "Running…" : "Predict"}
            </SubmitBtn>
          </>
        )}
        {predictError && <ErrorBox style={{ marginTop: "1rem" }}>{predictError}</ErrorBox>}
        {predictResult && !predictResult.error && (
          <PredictResult theme={theme} style={{ marginTop: "1rem" }}>
            <strong>Prediction:</strong> {predictResult.prediction != null ? Number(predictResult.prediction) : "—"}
            {predictResult.probability != null && (
              <> &nbsp;|&nbsp; <strong>P(y=1):</strong> {Number(predictResult.probability).toFixed(4)}</>
            )}
          </PredictResult>
        )}
      </WhatIfCard>

      <SectionTitle theme={theme}>Team view: all players, features, and predictions</SectionTitle>
      <WhatIfCard theme={theme}>
        <p style={{ color: theme.secondaryText, fontSize: "0.9rem", marginBottom: "1rem" }}>
          Select a date and team to load every player on that team with their feature values and model predictions.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end", marginBottom: "1rem" }}>
          <Field theme={theme}>
            <label>Date</label>
            <input type="date" value={teamViewDate} onChange={(e) => setTeamViewDate(e.target.value)} />
          </Field>
          <Field theme={theme}>
            <label>Team</label>
            <select
              value={teamViewTeamId}
              onChange={(e) => setTeamViewTeamId(e.target.value)}
              aria-label="Team"
            >
              <option value="">Select team</option>
              {teamOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>
          <Field theme={theme}>
            <label>Model</label>
            <select value={teamViewModel} onChange={(e) => setTeamViewModel(e.target.value)} aria-label="Model for team">
              {MODEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>
          <SubmitBtn type="button" theme={theme} onClick={fetchTeamView} disabled={teamViewLoading}>
            {teamViewLoading ? "Loading…" : "Load team"}
          </SubmitBtn>
        </div>
        {teamViewError && <ErrorBox style={{ marginBottom: "1rem" }}>{teamViewError}</ErrorBox>}
        {teamViewResult && teamViewResult.players?.length > 0 && (
          <>
            <Summary theme={theme} style={{ marginBottom: "0.75rem" }}>
              <strong>Team:</strong> {teamViewResult.team_name ?? teamViewResult.team_id} &nbsp;|&nbsp;{" "}
              <strong>Date:</strong> {teamViewResult.date} &nbsp;|&nbsp;{" "}
              {teamViewResult.players.length} player{teamViewResult.players.length !== 1 ? "s" : ""}
              {teamViewResult.lineups_posted != null && (
                <> &nbsp;|&nbsp; <strong>Lineups posted:</strong> {teamViewResult.lineups_posted ? "Yes" : "No"}</>
              )}
              {teamViewResult.lineups_last_updated && (
                <> &nbsp;|&nbsp; <strong>Lineups last updated:</strong> {teamViewResult.lineups_last_updated}</>
              )}
            </Summary>
            {teamViewResult.players[0].models.length > 1 && (
              <Field theme={theme} style={{ marginBottom: "0.75rem" }}>
                <label>Display model</label>
                <select
                  value={teamViewDisplayModel}
                  onChange={(e) => setTeamViewDisplayModel(e.target.value)}
                  style={{ minWidth: "180px" }}
                >
                  {teamViewResult.players[0].models.map((m) => (
                    <option key={m.model_type} value={m.model_type}>{m.model_type}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field theme={theme} style={{ marginBottom: "0.75rem" }}>
              <label>Show</label>
              <select
                value={teamViewLineupFilter}
                onChange={(e) => setTeamViewLineupFilter(e.target.value)}
                style={{ minWidth: "140px" }}
                aria-label="Filter by lineup"
              >
                <option value="all">All players</option>
                <option value="lineup">In lineup only</option>
              </select>
            </Field>
            <div style={{ marginBottom: "0.75rem" }}>
              <SubmitBtn type="button" theme={theme} onClick={downloadTeamViewCsv}>
                Download table as CSV
              </SubmitBtn>
            </div>
            <div style={{ overflowX: "auto" }}>
              <TeamTable theme={theme}>
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Lineup</th>
                    <th>Game ID</th>
                    {(() => {
                      const player = teamViewResult.players[0];
                      const modelData = player.models.find((m) => m.model_type === teamViewDisplayModel) || player.models[0];
                      const features = modelData?.features || [];
                      return (
                        <>
                          {features.map((f) => (
                            <th key={f.feature_name} className="num" title={f.feature_name}>
                              {f.feature_name.length > 12 ? f.feature_name.slice(0, 10) + "…" : f.feature_name}
                            </th>
                          ))}
                          <th className="num">Prediction</th>
                          <th className="num">P(y=1)</th>
                        </>
                      );
                    })()}
                  </tr>
                </thead>
                <tbody>
                  {teamViewResult.players
                    .filter((p) => teamViewLineupFilter !== "lineup" || p.in_lineup === true)
                    .map((p) => {
                    const modelData = p.models.find((m) => m.model_type === teamViewDisplayModel) || p.models[0];
                    const features = modelData?.features || [];
                    return (
                      <tr key={`${p.player_id}-${p.game_id}`}>
                        <td>{p.player_name ?? p.player_id ?? "—"}</td>
                        <td>{p.batting_order != null ? p.batting_order : "—"}</td>
                        <td>{p.game_id ?? "—"}</td>
                        {features.map((f) => (
                          <td key={f.feature_name} className="num">
                            {f.value != null ? Number(f.value).toFixed(4) : "—"}
                          </td>
                        ))}
                        <td className="num">{modelData?.prediction != null ? Number(modelData.prediction) : "—"}</td>
                        <td className="num">{modelData?.probability != null ? Number(modelData.probability).toFixed(4) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </TeamTable>
            </div>
          </>
        )}
      </WhatIfCard>
    </Container>
  );
}

export default AdminModelDebugPage;
